import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Plus, Users, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Panel as ResizablePanel, PanelGroup as ResizablePanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Avatar } from '../components/Profile/Avatar';
import { ChatCard } from '../components/ui/ChatCard';
import { ConversationList } from '../components/Chat/ConversationList';
import { Auth } from '../components/Auth';
import { CreateGroupChat } from '../components/Chat/CreateGroupChat';
import { EditGroupChat } from '../components/Chat/EditGroupChat';
import { GroupChatMembers } from '../components/Chat/GroupChatMembers';
import {
  startConversation,
  getConversationMessages,
  sendMessage,
  sendVoiceMessage,
  getConversations,
  subscribeToConversation,
  subscribeToConversationUpdates,
  deleteMessage,
  subscribeToMessageDeletion
} from '../lib/chat';
import { getUsersWithOnlineStatus } from '../lib/activity';
import { supabase } from '../lib/supabase';

export function Chat() {
  const { userId } = useParams<{ userId: string }>();
  const [routerSearchParams] = useSearchParams();
  const groupId = routerSearchParams.get('conversation');

  const navigate = useNavigate();
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const subscriptionRef = useRef<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [conversationSubscription, setConversationSubscription] = useState<any>(null);
  const [messageDeletionSubscription, setMessageDeletionSubscription] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [switchingChat, setSwitchingChat] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const hasActiveChat = !!(userId || groupId);

  useEffect(() => {
    return () => {
      // Use ref for reliable cleanup (avoids stale closure)
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
      if (conversationSubscription) conversationSubscription.unsubscribe();
      if (messageDeletionSubscription) messageDeletionSubscription.unsubscribe();
    };
  }, [subscription, conversationSubscription, messageDeletionSubscription]);

  useEffect(() => {
    const setupConversationUpdates = async () => {
      try {
        const sub = await subscribeToConversationUpdates((conversationId: string) => {
          setConversations(prev => {
            const conversation = prev.find(c => c.id === conversationId);
            if (!conversation) return prev;
            const filtered = prev.filter(c => c.id !== conversationId);
            return [conversation, ...filtered];
          });
        });
        setConversationSubscription(sub);
      } catch (error) {
        console.error('Error setting up conversation updates:', error);
      }
    };
    setupConversationUpdates();
  }, []);

  const handleSelectConversation = async (conversation: any) => {
    setMessages([]);
    setConversationId(conversation.id);
    setSwitchingChat(true);
    setLoadingProgress(0);
    
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => Math.min(prev + 10, 90));
    }, 100);

    try {
      if (conversation.type === 'direct') {
        setOtherUser(conversation.otherUser);
        navigate(`/chat/${conversation.otherUser.id}`);
      } else {
        setOtherUser({
          id: conversation.id,
          username: conversation.name || 'Group Chat',
          avatar_url: conversation.image_url,
          isGroup: true,
          participants: conversation.participants,
          admin_id: conversation.admin_id
        });
        navigate(`/chat?conversation=${conversation.id}`);
      }

      const initialMessages = await getConversationMessages(conversation.id, null, 30);
      setMessages(initialMessages);
      
      // Unsubscribe from previous conversation using ref (avoids stale closure)
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
      
      const sub = await subscribeToConversation(
        conversation.id,
        (newMessage: any) => {
          if (!newMessage.sender.isCurrentUser) {
            setMessages(prev => {
              // Prevent duplicate if optimistic message already present
              if (prev.some(m => m.id === newMessage.id)) return prev;
              return [...prev, newMessage];
            });
          }
        }
      );
      subscriptionRef.current = sub;
      setSubscription(sub);
    } catch (error) {
      console.error('Error switching conversation:', error);
      setError('Failed to load conversation');
    } finally {
      clearInterval(progressInterval);
      setLoadingProgress(100);
      setTimeout(() => {
        setSwitchingChat(false);
        setLoadingProgress(0);
      }, 500);
    }
  };

  const handleConversationUpdate = (updatedConversations: any[]) => {
    setConversations(updatedConversations);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const message = params.get('message');
    if (message) {
      setInitialMessage(message);
      params.delete('message');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    // Cancel any previous in-flight init when userId/groupId changes
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const initializeChat = async () => {
      try {
        if (controller.signal.aborted) return;
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (controller.signal.aborted) return;
        if (authError || !user) {
          setIsAuthenticated(false);
          setLoading(false);
          if (userId) setShowAuthModal(true);
          return;
        }
        
        setIsAuthenticated(true);

        const usersToFetch = userId ? [user.id, userId] : [user.id];
        const usersWithStatus = await getUsersWithOnlineStatus(usersToFetch);
        const currentUserData = usersWithStatus[user.id];
        const otherUserData = userId ? usersWithStatus[userId] : null;

        if (!currentUserData) {
          setError('Failed to load user profile');
          setLoading(false);
          return;
        }

        setCurrentUser({
          id: user.id,
          username: currentUserData.username,
          avatar_url: currentUserData.avatar_url
        });

        if (controller.signal.aborted) return;

        try {
          const userConversations = await getConversations();
          if (controller.signal.aborted) return;
          setConversations(userConversations);

          if (groupId) {
            const conversation = userConversations.find((c: any) => c.id === groupId);
            if (!conversation) {
              setError('Group chat not found');
              setLoading(false);
              return;
            }

            setConversationId(groupId);
            if (conversation.type === 'group') {
              setOtherUser({
                id: groupId,
                username: conversation.name || 'Group Chat',
                avatar_url: conversation.image_url,
                isGroup: true,
                participants: conversation.participants,
                admin_id: conversation.admin_id
              });
            } else if (conversation.otherUser) {
              setOtherUser({
                id: conversation.otherUser.id,
                username: conversation.otherUser.username,
                avatar_url: conversation.otherUser.avatar_url,
                isGroup: false,
                isOnline: conversation.otherUser.isOnline
              });
            } else {
              setError('Conversation is unavailable');
              setLoading(false);
              return;
            }

            try {
              const initialMessages = await getConversationMessages(groupId, null, 30);
              setMessages(initialMessages);
            } catch (err) {
              console.error('Error fetching messages:', err);
              setError('Failed to load messages');
            }

            try {
              const sub = await subscribeToConversation(groupId, (newMessage: any) => {
                if (!newMessage.sender.isCurrentUser) {
                  setMessages(prev => {
                    if (prev.some(m => m.id === newMessage.id)) return prev;
                    return [...prev, newMessage];
                  });
                }
              });
              subscriptionRef.current = sub;
              setSubscription(sub);
            } catch (err) {
              console.error('Error setting up subscription:', err);
            }
          }
        } catch (err) {
          console.error('Error fetching conversations:', err);
          setError('Failed to load conversations');
        }

        if (controller.signal.aborted) return;

        if (userId) {
          if (!otherUserData) {
            setError('User not found');
            setLoading(false);
            return;
          }

          setOtherUser({
            id: userId,
            username: otherUserData.username,
            avatar_url: otherUserData.avatar_url,
            isOnline: otherUserData.isOnline
          });

          try {
            const conversation = await startConversation(userId);
            if (!conversation) {
              setError('Failed to start conversation');
              setLoading(false);
              return;
            }

            setConversationId(conversation.id);

            try {
              const initialMessages = await getConversationMessages(conversation.id, null, 30);
              setMessages(initialMessages);
            } catch (err: any) {
              setError(err.message || 'Failed to load messages');
              setLoading(false);
              return;
            }

            try {
              await supabase
                .from('conversations')
                .update({ last_viewed_by: user.id, last_viewed_at: new Date().toISOString() })
                .eq('id', conversation.id);
            } catch (err) {
              console.error('Failed to update last viewed:', err);
            }

            try {
              const sub = await subscribeToConversation(conversation.id, (newMessage: any) => {
                if (!newMessage.sender.isCurrentUser) {
                  setMessages(prev => {
                    if (prev.some(m => m.id === newMessage.id)) return prev;
                    return [...prev, newMessage];
                  });
                }
              });
              subscriptionRef.current = sub;
              setSubscription(sub);
            } catch (err: any) {
              setError(err.message || 'Failed to connect to real-time updates');
              setLoading(false);
              return;
            }
          } catch (err: any) {
            setError(err.message || 'Failed to start conversation');
            setLoading(false);
            return;
          }
        }
      } catch (err: any) {
        setError('Failed to initialize chat');
      } finally {
        setLoading(false);
        setLoadingConversation(false);
      }
    };

    initializeChat();

    return () => {
      controller.abort();
    };
  }, [userId, groupId]);

  useEffect(() => {
    if (userId || groupId) {
      setLoadingConversation(true);
      const timer = setTimeout(() => setLoadingConversation(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [userId]);

  useEffect(() => {
    if (!conversationId || !currentUser?.id) return;

    const updateLastViewed = async () => {
      try {
        await supabase
          .from('conversations')
          .update({ last_viewed_by: currentUser.id, last_viewed_at: new Date().toISOString() })
          .eq('id', conversationId)
          .filter('participant_ids', 'cs', `{${currentUser.id}}`);
      } catch (err) {
        console.error('Failed to update last viewed:', err);
      }
    };

    updateLastViewed();
    const interval = setInterval(updateLastViewed, 30000);
    return () => clearInterval(interval);
  }, [conversationId, currentUser?.id]);

  // Set up message deletion subscription when conversation changes
  useEffect(() => {
    if (!conversationId) return;

    // Clean up previous subscription
    if (messageDeletionSubscription) {
      messageDeletionSubscription.unsubscribe();
    }

    const setupMessageDeletionSubscription = async () => {
      const sub = await subscribeToMessageDeletion(conversationId, (messageId: string) => {
        // Remove the deleted message from the UI
        setMessages(prev => {
          const remainingMessages = prev.filter(m => m.id !== messageId);

          // Update conversation list with the remaining messages
          setConversations(convs => {
            return convs.map(c =>
              c.id === conversationId
                ? {
                    ...c,
                    lastMessage: remainingMessages.length > 0
                      ? (remainingMessages[remainingMessages.length - 1]?.content || null)
                      : null,
                    lastMessageAt: remainingMessages.length > 0
                      ? (remainingMessages[remainingMessages.length - 1]?.created_at || null)
                      : null
                  }
                : c
            );
          });

          return remainingMessages;
        });
      });
      setMessageDeletionSubscription(sub);
    };

    setupMessageDeletionSubscription();

    return () => {
      if (messageDeletionSubscription) {
        messageDeletionSubscription.unsubscribe();
      }
    };
  }, [conversationId]);

  const handleDeleteMessage = async (messageId: string) => {
    if (!conversationId) return;

    try {
      await deleteMessage(messageId, conversationId);

      // Remove the message from the UI immediately
      setMessages(prev => {
        const remainingMessages = prev.filter(m => m.id !== messageId);

        // Update conversation list with the remaining messages
        setConversations(convs => {
          return convs.map(c =>
            c.id === conversationId
              ? {
                  ...c,
                  lastMessage: remainingMessages.length > 0
                    ? (remainingMessages[remainingMessages.length - 1]?.content || null)
                    : null,
                  lastMessageAt: remainingMessages.length > 0
                    ? (remainingMessages[remainingMessages.length - 1]?.created_at || null)
                    : null
                }
              : c
          );
        });

        return remainingMessages;
      });
    } catch (error: any) {
      console.error('Error deleting message:', error);
      toast.error(error.message || 'Failed to delete message');
    }
  };

  const handleSendMessage = async (content: string, replyToId?: string) => {
    if (!conversationId || !content.trim()) return;
    const tempId = crypto.randomUUID();
    const replyMessage = replyToId ? messages.find(m => m.id === replyToId) : null;

    try {
      const localMessage = {
        id: tempId,
        content,
        created_at: new Date().toISOString(),
        sender: {
          id: currentUser?.id || '',
          username: currentUser?.username || 'You',
          avatar_url: currentUser?.avatar_url,
          isOnline: true,
          isCurrentUser: true
        },
        timestamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
        status: 'sent',
        replyTo: replyMessage ? {
          id: replyMessage.id,
          content: replyMessage.messageType === 'voice' ? '🎤 Voice message' : replyMessage.content,
          senderUsername: replyMessage.sender.username
        } : undefined
      };

      setMessages(prev => [...prev, localMessage]);
      await sendMessage(conversationId, content, replyToId);

      setConversations(prev => {
        const currentConv = prev.find(c => c.id === conversationId);
        if (!currentConv) return prev;
        const filtered = prev.filter(c => c.id !== conversationId);
        return [{ ...currentConv, lastMessage: content, lastMessageAt: new Date().toISOString() }, ...filtered];
      });
    } catch (err) {
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      throw new Error('Failed to send message. Please try again.');
    }
  };

  const handleSendVoiceMessage = async (audioBlob: Blob, duration: number) => {
    if (!conversationId) return;
    const tempId = crypto.randomUUID();
    try {
      const localMessage = {
        id: tempId,
        content: '🎤 Voice message',
        messageType: 'voice',
        audioUrl: URL.createObjectURL(audioBlob),
        audioDuration: duration,
        created_at: new Date().toISOString(),
        sender: {
          id: currentUser?.id || '',
          username: currentUser?.username || 'You',
          avatar_url: currentUser?.avatar_url,
          isOnline: true,
          isCurrentUser: true
        },
        timestamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }),
        status: 'sent'
      };

      setMessages(prev => [...prev, localMessage]);
      const sentMessage = await sendVoiceMessage(conversationId, audioBlob, duration);
      // Revoke the local blob URL to prevent memory leak
      URL.revokeObjectURL(localMessage.audioUrl);
      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? { ...msg, id: sentMessage.id, messageType: 'voice', audioUrl: sentMessage.audio_url, audioDuration: sentMessage.audio_duration || duration } : msg
      ));
      setConversations(prev => {
        const currentConv = prev.find(c => c.id === conversationId);
        if (!currentConv) return prev;
        const filtered = prev.filter(c => c.id !== conversationId);
        return [{ ...currentConv, lastMessage: '🎤 Voice message', lastMessageAt: new Date().toISOString() }, ...filtered];
      });
    } catch (err) {
      // Revoke blob URL on failure too to prevent memory leak
      URL.revokeObjectURL(localMessage.audioUrl);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      throw new Error('Failed to send voice message. Please try again.');
    }
  };

  // --- RENDER ---

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
        {showAuthModal && <Auth onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-content mb-4">{error}</h2>
          <Link to="/" className="text-accent-text hover:text-accent-text/80">Return to Home</Link>
          {showAuthModal && <Auth onClose={() => setShowAuthModal(false)} />}
        </div>
      </div>
    );
  }

  if (isAuthenticated === false && userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F3F7EE] to-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-8">
            <div className="p-4 bg-accent-text/10 rounded-full">
              <MessageSquare className="h-12 w-12 text-accent-text" />
            </div>
          </div>
          <h1 className="text-4xl font-gelica font-bold text-content mb-6">Sign in to Message</h1>
          <p className="text-xl text-content/70 mb-8">Please sign in to start a conversation with this user.</p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg text-white bg-accent-text hover:bg-accent-text/90 transition-colors"
          >
            Sign in to Continue
          </button>
        </div>
        {showAuthModal && <Auth onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  // Loading overlay
  const loadingOverlay = (loadingConversation || switchingChat) ? (
    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center rounded-r-2xl">
      <div className="flex flex-col items-center space-y-3">
        <div className="relative">
          <div className="w-12 h-12 border-3 border-accent-text/20 rounded-full"></div>
          <div className="absolute top-0 left-0 w-12 h-12 border-3 border-accent-text border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-sm font-medium text-content/70">
          {switchingChat ? 'Loading conversation...' : 'Connecting...'}
        </p>
        {switchingChat && (
          <div className="w-32 bg-gray-200 rounded-full h-1 overflow-hidden">
            <div className="h-full bg-accent-text rounded-full transition-all duration-300" style={{ width: `${loadingProgress}%` }} />
          </div>
        )}
      </div>
    </div>
  ) : null;

  // Chat header
  const chatHeader = otherUser ? (
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white flex-shrink-0 lg:rounded-tr-2xl">
      <div className="flex items-center gap-3">
        <Link to="/chat" className="lg:hidden p-1.5 -ml-1 text-content/50 hover:text-content hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        {otherUser.isGroup ? (
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
            </div>
            <div>
              <button onClick={() => setShowEditGroup(true)} className="font-semibold text-sm text-content hover:text-accent-text transition-colors text-left">
                {otherUser.username}
              </button>
              <button onClick={() => setShowGroupMembers(true)} className="text-xs text-content/50 hover:text-accent-text transition-colors flex items-center gap-1">
                <Users size={12} />
                {otherUser.participants ? `${otherUser.participants.length} members` : 'Loading...'}
              </button>
            </div>
          </div>
        ) : (
          <Link to={`/profile/${otherUser.username}/listings`} className="flex items-center gap-3">
            <div className="relative">
              <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
              <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${otherUser.isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
            </div>
            <div>
              <span className="font-semibold text-sm text-content">{otherUser.username}</span>
              <div className="text-xs text-content/50">{otherUser.isOnline ? 'Online' : 'Offline'}</div>
            </div>
          </Link>
        )}
      </div>
    </div>
  ) : null;

  // Chat view
  const chatView = hasActiveChat && otherUser && currentUser ? (
    <div className="flex flex-col h-full relative">
      {/* Mobile: simplified header with back button */}
      <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white sticky top-0 z-10">
        <button
          onClick={() => navigate('/chat')}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-content" />
        </button>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {otherUser.isGroup ? (
            <button type="button" onClick={() => { console.log('Opening group members'); setShowGroupMembers(true); }}>
              <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
            </button>
          ) : (
            <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
          )}
          <div className="min-w-0">
            <h3 className="font-semibold text-content text-sm truncate">{otherUser.username}</h3>
          </div>
        </div>
      </div>
      {/* Desktop: full header */}
      <div className="hidden lg:flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white flex-shrink-0 lg:rounded-tr-2xl">
        <div className="flex items-center gap-3">
          {otherUser.isGroup ? (
            <div className="flex items-center gap-3">
              <button onClick={() => setShowGroupMembers(true)}>
                <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
              </button>
              <div>
                <span className="font-semibold text-sm text-content">{otherUser.username}</span>
                <div className="text-xs text-content/50">{otherUser.participants?.length || 0} members</div>
              </div>
            </div>
          ) : (
            <Link to={`/profile/${otherUser.username}/listings`} className="flex items-center gap-3">
              <Avatar url={otherUser.avatar_url} size="sm" userId={otherUser.id} editable={false} />
              <span className="font-semibold text-sm text-content">{otherUser.username}</span>
            </Link>
          )}
        </div>
      </div>
      {loadingOverlay}
      <div className="flex-1 min-h-0">
        <ChatCard
          initialMessages={messages}
          className="w-full h-full"
          initialMessage={initialMessage}
          onSendMessage={handleSendMessage}
          onSendVoiceMessage={handleSendVoiceMessage}
          onDeleteMessage={handleDeleteMessage}
          onLoadMore={async (oldestMessageCreatedAt) => {
            if (!conversationId) return [];
            return getConversationMessages(conversationId, oldestMessageCreatedAt, 30);
          }}
        />
      </div>

      {/* Group Members Modal - inside chat view */}
      {showGroupMembers && otherUser?.isGroup && conversationId && (
        <GroupChatMembers conversationId={conversationId} onClose={() => setShowGroupMembers(false)} isAdmin={currentUser?.id === otherUser.admin_id} />
      )}
    </div>
  ) : null;

  // Empty state for desktop
  const emptyState = (
    <div className="h-full flex flex-col items-center justify-center text-center px-8">
      <div className="w-16 h-16 bg-accent-text/5 rounded-2xl flex items-center justify-center mb-5">
        <MessageSquare className="h-7 w-7 text-accent-text/40" />
      </div>
      <h2 className="text-lg font-semibold text-content mb-1.5">Your messages</h2>
      <p className="text-sm text-content/50 max-w-[240px]">
        Select a conversation to start chatting or send a new message
      </p>
    </div>
  );

  return (
    <>
      {/* Mobile layout — full screen when chat is active, otherwise with header/bottom nav spacing */}
      {hasActiveChat ? (
        // Full screen overlay - above header and bottom nav
        <div className="lg:hidden fixed inset-0 z-[999] bg-white flex flex-col">
          {chatView}
        </div>
      ) : (
        <div className="flex flex-col lg:hidden" style={{ height: 'calc(100dvh - 4rem - 4rem)' }}>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ConversationList
              conversations={conversations}
              activeConversationId={conversationId}
              onSelect={handleSelectConversation}
              onConversationUpdate={handleConversationUpdate}
              onCreateGroup={() => setShowCreateGroup(true)}
              triggerNewMessage={showNewMessage}
              onNewMessageHandled={() => setShowNewMessage(false)}
            />

            {/* Mobile Floating Action Button */}
            <button
              onClick={() => setShowNewMessage(true)}
              className="lg:hidden fixed bottom-24 right-6 w-14 h-14 bg-accent-text text-white rounded-full shadow-lg flex items-center justify-center hover:bg-accent-text/90 transition-all z-40"
              style={{ bottom: 'calc(4rem + 1.5rem)' }}
            >
              <Plus className="h-7 w-7" />
            </button>
          </div>
        </div>
      )}

      {/* Desktop layout — boxed container, fills most of viewport height */}
      <div className="hidden lg:flex flex-col p-3 xl:p-4" style={{ height: 'calc(100dvh - 4rem - 2rem)' }}>
        <div className="w-full max-w-page mx-auto bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-96 xl:w-[420px] flex-shrink-0 border-r border-gray-100">
            <ConversationList
              conversations={conversations}
              activeConversationId={conversationId}
              onSelect={handleSelectConversation}
              onConversationUpdate={handleConversationUpdate}
              onCreateGroup={() => setShowCreateGroup(true)}
              triggerNewMessage={showNewMessage}
              onNewMessageHandled={() => setShowNewMessage(false)}
            />
          </div>

          {/* Chat area */}
          <div className="flex-1 min-w-0">
            {hasActiveChat ? chatView : emptyState}
          </div>
        </div>
      </div>
      
      {showAuthModal && <Auth onClose={() => setShowAuthModal(false)} />}

      {showCreateGroup && (
        <CreateGroupChat
          onClose={() => setShowCreateGroup(false)}
          onSuccess={(conversationId) => { setShowCreateGroup(false); navigate(`/chat?conversation=${conversationId}`); }}
        />
      )}

      {showEditGroup && otherUser?.isGroup && (
        <EditGroupChat
          conversationId={conversationId!}
          currentName={otherUser.username}
          currentImage={otherUser.avatar_url}
          onClose={() => setShowEditGroup(false)}
          onSuccess={(newName, newImage) => { setOtherUser((prev: any) => ({ ...prev!, username: newName, avatar_url: newImage })); setShowEditGroup(false); }}
        />
      )}
    </>
  );
}

export default Chat;
